import React, { useRef, useEffect } from "react";

// React wrapper for Om Design System Select web component
const OmSelect = React.forwardRef(({ 
  children,
  label,
  name,
  required = false,
  value,
  onChange,
  onInput,
  onFocus,
  onBlur,
  ...props 
}, ref) => {
  const elementRef = useRef(null);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const addEventListeners = () => {
      const handleChange = (event) => {
        if (onChange) {
          onChange(event);
        }
      };

      const handleInput = (event) => {
        if (onInput) {
          onInput(event);
        }
      };

      const handleFocus = (event) => {
        if (onFocus) {
          onFocus(event);
        }
      };

      const handleBlur = (event) => {
        if (onBlur) {
          onBlur(event);
        }
      };

      try {
        element.addEventListener('change', handleChange);
        element.addEventListener('input', handleInput);
        element.addEventListener('focus', handleFocus);
        element.addEventListener('blur', handleBlur);
        
        return () => {
          if (element) {
            element.removeEventListener('change', handleChange);
            element.removeEventListener('input', handleInput);
            element.removeEventListener('focus', handleFocus);
            element.removeEventListener('blur', handleBlur);
          }
        };
      } catch (error) {
        console.warn('Failed to attach event listeners to OmSelect:', error);
        return () => {};
      }
    };

    // Check if component is ready or wait for it
    if (element.shadowRoot || element.isConnected) {
      return addEventListeners();
    } else {
      // Wait for component to be ready
      const timeout = setTimeout(addEventListeners, 100);
      return () => clearTimeout(timeout);
    }
  }, [onChange, onInput, onFocus, onBlur]);

  // Set value when it changes
  useEffect(() => {
    const element = elementRef.current;
    if (element && value !== undefined && element.value !== value) {
      element.value = value;
    }
  }, [value]);

  return (
    <omk-select
      ref={(el) => {
        elementRef.current = el;
        if (ref) {
          if (typeof ref === 'function') {
            ref(el);
          } else {
            ref.current = el;
          }
        }
      }}
      label={label}
      name={name}
      required={required}
      {...props}
    >
      {children}
    </omk-select>
  );
});

OmSelect.displayName = 'OmSelect';

export default OmSelect;
